/**
 * custom-cart-upsell.js
 *
 * Renders product recommendations inside the cart drawer upsell section.
 *
 * Listens to `custom:cart:updated` (from CartManager) to refresh suggestions
 * using the cart items already present in the event — no extra /cart.js fetch.
 *
 * Add-to-cart delegates to window.CartManager.add() so the full
 * cycle (gift sync, progress update, etc.) fires once per action.
 *
 * Dependencies:
 *   - #cart-upsell container rendered by custom-cart-upsell.liquid
 *   - window.CartManager (custom-cart-manager.js)
 */

(function () {
  // Guard: attach global listener only once — the DOM may be morphed but
  // modules are not re-executed, so the listener persists correctly.
  if (window._cartUpsellInit) return;
  window._cartUpsellInit = true;

  // ── Helpers ──────────────────────────────────────────────────────────────

  function _formatMoney(cents) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
      .format(Number(cents || 0) / 100);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  /**
   * Builds a single upsell item element.
   * @param {object} product      - Shopify product object from recommendations API.
   * @param {Set<number>} cartIds - Set of product IDs currently in the cart.
   * @returns {HTMLElement|null}
   */
  function _buildItem(product, cartIds) {
    const availableVariants = product.variants.filter(v => v.available);
    if (!availableVariants.length) return null;

    const firstVariant  = availableVariants[0];
    const price         = Number(firstVariant.price);
    const comparePrice  = Number(firstVariant.compare_at_price || 0);
    const showCompare   = comparePrice > price;

    const rawImg  = product.featured_image;
    const imgRaw  = rawImg ? (rawImg.src || rawImg) : '';
    const imgSrc  = imgRaw
      ? imgRaw.split('?')[0] + '?width=56&height=56&crop=center'
      : '';

    const item = document.createElement('div');
    item.className = 'cart-upsell__item';
    item.dataset.upsellProductId = String(product.id);

    // Image
    const imgWrap = document.createElement('a');
    imgWrap.href = product.url;
    imgWrap.className = 'cart-upsell__image';
    if (imgSrc) {
      const img = document.createElement('img');
      img.src    = imgSrc;
      img.alt    = product.title;
      img.width  = 56;
      img.height = 56;
      img.loading = 'lazy';
      imgWrap.appendChild(img);
    }

    // Content
    const content = document.createElement('div');
    content.className = 'cart-upsell__content';

    const name = document.createElement('a');
    name.className   = 'cart-upsell__name';
    name.href        = product.url;
    name.textContent = product.title;

    const priceWrap = document.createElement('span');
    priceWrap.className = 'cart-upsell__price';

    const compareEl = document.createElement('del');
    compareEl.className = 'cart-upsell__compare-price' + (showCompare ? '' : ' hidden');
    if (showCompare) compareEl.textContent = _formatMoney(comparePrice);

    const currentEl = document.createElement('span');
    currentEl.className   = 'cart-upsell__current-price';
    currentEl.textContent = _formatMoney(price);

    priceWrap.appendChild(compareEl);
    priceWrap.appendChild(currentEl);
    content.appendChild(name);
    content.appendChild(priceWrap);

    // Variant select (only when multiple available variants exist)
    let selectedVariantId = String(firstVariant.id);
    if (availableVariants.length > 1) {
      const label = document.createElement('label');
      label.className = 'visually-hidden';
      label.setAttribute('for', `upsell-select-${product.id}`);
      label.textContent = 'Variante';

      const select = document.createElement('select');
      select.className = 'cart-upsell__variant-select';
      select.id        = `upsell-select-${product.id}`;

      availableVariants.forEach(v => {
        const opt = document.createElement('option');
        opt.value           = String(v.id);
        opt.textContent     = v.title;
        opt.dataset.price   = v.price;
        opt.dataset.compare = v.compare_at_price || 0;
        select.appendChild(opt);
      });

      select.addEventListener('change', () => {
        const opt = select.options[select.selectedIndex];
        const p   = Number(opt.dataset.price);
        const cp  = Number(opt.dataset.compare);
        currentEl.textContent = _formatMoney(p);
        if (cp > p) {
          compareEl.textContent = _formatMoney(cp);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.classList.add('hidden');
        }
        selectedVariantId = opt.value;
      });

      content.appendChild(label);
      content.appendChild(select);
    }

    // Add-to-cart button
    const addBtn = document.createElement('button');
    addBtn.type      = 'button';
    addBtn.className = 'button cart-upsell__add-btn';
    addBtn.innerHTML = '<span aria-hidden="true">+</span> Adicionar';

    addBtn.addEventListener('click', () => {
      if (addBtn.disabled) return;
      addBtn.disabled = true;

      const manager = window.CartManager;
      if (!manager) {
        console.error('[CartUpsell] CartManager not available');
        addBtn.disabled = false;
        return;
      }

      manager.add(Number(selectedVariantId), 1).finally(() => {
        addBtn.disabled = false;
      });
    });

    item.appendChild(imgWrap);
    item.appendChild(content);
    item.appendChild(addBtn);
    return item;
  }

  /**
   * Fetches recommendations and renders upsell items into every #cart-upsell
   * instance. Multiple instances exist because header-actions is rendered 3
   * times in the header (desktop, mobile top, mobile menu) — we must update
   * all of them so the visible drawer, regardless of breakpoint, shows items.
   * @param {number[]} cartProductIds - IDs of products currently in the cart.
   */
  function _refresh(cartProductIds) {
    const containers = Array.from(document.querySelectorAll('#cart-upsell'));
    if (!containers.length) return;

    if (!cartProductIds.length) {
      containers.forEach(c => { c.hidden = true; });
      return;
    }

    const cartIdSet = new Set(cartProductIds.map(String));

    fetch(`/recommendations/products.json?product_id=${cartProductIds[0]}&limit=10&intent=related`)
      .then(r => r.json())
      .then(({ products = [] }) => {
        const filtered = products
          .filter(p => p.available && !cartIdSet.has(String(p.id)))
          .slice(0, 3);

        containers.forEach(container => {
          const list = container.querySelector('#cart-upsell__wrapper');
          if (!list) return;

          list.innerHTML = '';

          if (!filtered.length) {
            container.hidden = true;
            return;
          }

          // Build fresh item elements for each container (event listeners
          // cannot be shared across DOM nodes via cloning).
          filtered
            .map(p => _buildItem(p, cartIdSet))
            .filter(Boolean)
            .forEach(el => list.appendChild(el));

          container.hidden = false;
        });
      })
      .catch(() => {
        containers.forEach(c => { c.hidden = true; });
      });
  }

  // ── Event listeners ───────────────────────────────────────────────────────

  // Primary: react to CartManager’s unified event — cart items are in the event
  document.addEventListener('custom:cart:updated', event => {
    const items = event.detail?.cart?.items;
    if (!items) return;
    const ids = items.map(item => item.product_id);
    requestAnimationFrame(() => _refresh(ids));
  });

  // Initial render on page load.
  // Modules are deferred (execute after HTML parsing), so document.readyState
  // is 'interactive' or 'complete' here — DOMContentLoaded may or may not have
  // fired yet depending on browser fetch scheduling with fetchpriority="low".
  // Using readyState guards both cases correctly.
  function _initUpsell() {
    // All containers have the same cart product IDs (same cart session), so
    // reading from the first is sufficient — _refresh updates all of them.
    const containers = Array.from(document.querySelectorAll('#cart-upsell'));
    if (!containers.length) return;
    const idsAttr = containers[0].dataset.cartProductIds || '';
    const ids     = idsAttr.split(',').map(id => parseInt(id.trim(), 10)).filter(Boolean);
    _refresh(ids);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _initUpsell);
  } else {
    _initUpsell();
  }

  // ── Fallback: re-populate when any cart drawer opens ─────────────────────
  // Handles mobile edge-cases where _initUpsell ran before the DOM was ready.
  // Must listen on ALL cart-drawer-component instances (3 in the header).
  function _hookCartDrawerOpen() {
    document.querySelectorAll('cart-drawer-component').forEach(drawer => {
      drawer.addEventListener('dialog:open', () => {
        // Check if any upsell wrapper is still empty.
        const anyEmpty = [...document.querySelectorAll('#cart-upsell')]
          .some(c => {
            const list = c.querySelector('#cart-upsell__wrapper');
            return list && list.children.length === 0;
          });
        if (!anyEmpty) return;
        _initUpsell();
      });
    });
  }
  _hookCartDrawerOpen();
})();
